import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';
import _ from 'underscore';

import { DataGridCells } from '~commonInfra/components/dataGrid/DataGridCells';
import { SortableHeader } from '~commonInfra/components/dataGrid/SortableHeader';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Set } from '~commonInfra/utils/Set';
import { SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { GenericActionsMenuItem } from '~src/commonInfra/components/GenericActionMenu';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { areFlowParametersEnabled } from '~src/flow/utils/FlowParametersUtils';

import { getAppStore } from '../../../bootstrap/redux/store/AppStore';
import { Messages } from '../../../codegen/Localize';
import { showRunFlowDialog } from '../../actions/RunFlowAction';
import { RunFlowOnOutputStepsAction } from '../../actions/RunFlowOnOutputStepsAction';
import { FlowDataGridCells, FlowParametersInOutputCell } from '../../FlowDataGridDefinitions';
import { FlowBaseInfo } from '../../models/FlowInfo';
import { FlowOutputStepInfo } from '../../models/FlowOutputStepInfo';
import { FlowOutputStepRunListItemInfo } from '../../models/FlowOutputStepRunInfo';
import { RunFlowTaskInfo } from '../../models/RunFlowTaskInfo';
import { FlowAllowedActions } from '../../serverApi/types/FlowAllowedActions';
import { writeOptionsFullMenuItemInfo, writeOptionsIncrementalMenuItemInfo, writeOptionsRunAllFullMenuItemInfo, writeOptionsRunAllIncrementalMenuItemInfo } from './RunFlowSubMenuItem';

export type RunOutputParameters = {
  readonly flowInfo: FlowBaseInfo;
  readonly outputId: string;
};

export type FlowOverviewDataGridRow = {
  readonly runOutput: RunOutputParameters;
  readonly flowOutputStep: FlowOutputStepInfo;
  readonly outputStepMostRecentRun: FlowOutputStepRunListItemInfo;
  readonly schedules: ReadonlyArray<RunFlowTaskInfo>;
};

export module FlowOverviewGrid {
  export function columns(
    flowInfo: FlowBaseInfo,
    flowAllowedActions: FlowAllowedActions,
    isServerOrSiteAdmin: boolean,
    siteUrls: SiteUrls,
    updateData: () => void,
    isIncrementalEnabled: boolean,
    schedulingFlowsEnabled: boolean,
    areParametersEnabled: boolean,
  ): DataGrid.Column[] {
    const flowFailureThresholdForRunPrevention = SessionServerSelectors.flowFailureThresholdForRunPrevention(getAppStore().getState());
    const runSingleOutputFull = (row: DataGrid.Row) => {
      RunFlowOnOutputStepsAction.dialog(row.data.runOutput.flowInfo, [row.data.runOutput.outputId], false)
        .then(() => updateData());
    };
    const runSingleOutputIncremental = (row: DataGrid.Row) => {
      RunFlowOnOutputStepsAction.dialog(row.data.runOutput.flowInfo, [row.data.runOutput.outputId], true)
        .then(() => updateData());
    };
    const runSingleOutputDefault = (row: DataGrid.Row) => {
      const data = row.data as FlowOverviewDataGridRow;
      const output = data.flowOutputStep;
      RunFlowOnOutputStepsAction.dialog(row.data.runOutput.flowInfo, [row.data.runOutput.outputId], output.isIncrementalDefault)
        .then(() => updateData());
    };
    const runAllOutputs = () => {
      showRunFlowDialog(Set.singletonForIdItem(flowInfo), null)
        .then(() => updateData());
    };
    const runAllOutputsFull = () => {
      showRunFlowDialog(Set.singletonForIdItem(flowInfo), false)
        .then(() => updateData());
    };
    const runAllOutputsIncremental = () => {
      showRunFlowDialog(Set.singletonForIdItem(flowInfo), true)
        .then(() => updateData());
    };

    const actions = (row: DataGrid.Row): ReadonlyArray<GenericActionsMenuItem> => {
      const data = row.data as FlowOverviewDataGridRow;
      const output = data.flowOutputStep;

      if (data.flowOutputStep.isIncrementalEnabled) {
        return [
          { menuItemInfo: writeOptionsFullMenuItemInfo(output), execute: () => runSingleOutputFull(row) },
          { menuItemInfo: writeOptionsIncrementalMenuItemInfo(output), execute: () => runSingleOutputIncremental(row) }
        ];
      }
      else {
        return null;
      }
    };

    const actionsRunAll: ReadonlyArray<GenericActionsMenuItem> = [
      { menuItemInfo: writeOptionsRunAllFullMenuItemInfo(), execute: runAllOutputsFull },
      { menuItemInfo: writeOptionsRunAllIncrementalMenuItemInfo(), execute: runAllOutputsIncremental }];

    const runOutputColumn: DataGrid.Column = {
      name: 'run-output-col',
      dataKey: 'runOutput',
      sortable: false,
      resizable: false,
      preferredWidth: 160,
      headerText: Messages.flowOverviewRunAll(),
      headerFormatter: DataGridCells.IconHeaderWithMenu(runAllOutputs, 'tb-prep-flow-overview-run-now-icon',
        FeatureFlags.incrementalRefresh && isIncrementalEnabled ? actionsRunAll : null),
      cellFormatter: DataGridCells.IconCellWithMenu({
        onIconClicked: FeatureFlags.incrementalRefresh ? runSingleOutputDefault : runSingleOutputFull,
        iconClass: 'tb-prep-flow-overview-run-now-icon',
        title: Messages.runNow(),
        label: Messages.flowOverviewRun(),
        actions: FeatureFlags.incrementalRefresh ? actions : null
      })
    };

    const outputStepColumn: DataGrid.Column = {
      name: 'output-step-col',
      dataKey: 'flowOutputStep',
      preferredWidth: CommonStyles.Sizes.ColMaxWidth, // If there is leftover space the column will expand up to this size)
      sortable: false,
      headerText: Messages.flowRunHistoryColumn_output(),
      direction: 'desc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: FlowDataGridCells.FlowOverviewOutputStepCell()
    };

    const flowParametersInOutputsColumn: DataGrid.Column = {
      name: 'flow-parameters-in-outputs',
      dataKey: 'flowParametersInOutputs',
      sortable: false,
      headerText: Messages.flowParametersHeader(),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: FlowParametersInOutputCell(),
    };

    const outputNameColumn: DataGrid.Column = {
      name: 'output-name-col',
      dataKey: 'outputName',
      sortable: false,
      headerText: Messages.flowOverviewOutputNameColumnName(),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: areFlowParametersEnabled() ? FlowDataGridCells.FlowDatasourcesForOutputCell(siteUrls) : FlowDataGridCells.FlowOverviewOutputNameCell(siteUrls)
    };

    const statusColumn: DataGrid.Column = {
      name: 'status-col',
      dataKey: 'status',
      sortable: false,
      headerText: Messages.status(),
      direction: 'desc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: FlowDataGridCells.FlowOverviewStatusCell(siteUrls, schedulingFlowsEnabled)
    };

    const scheduleColumn: DataGrid.Column = {
      name: 'schedule-col',
      dataKey: 'schedules',
      sortable: false,
      headerText: Messages.flowRunHistoryColumn_schedule(),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: FlowDataGridCells.FlowOverviewScheduleCell(siteUrls, flowAllowedActions.runFlow, isServerOrSiteAdmin, flowFailureThresholdForRunPrevention, updateData)
    };


    const errorColumn: DataGrid.Column = {
      name: 'error-col',
      dataKey: 'errors',
      sortable: false,
      headerText: Messages.flowOverviewErrors(),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: FlowDataGridCells.FlowOverviewErrorsCell(siteUrls, flowAllowedActions.download)
    };

    const flowRunNowAllowed = flowAllowedActions.runFlow;
    return _.compact([
      flowRunNowAllowed ? runOutputColumn : null,
      outputStepColumn,
      areParametersEnabled && (flowInfo.relevantParameters && flowInfo.relevantParameters.length > 0) ? flowParametersInOutputsColumn : null,
      outputNameColumn,
      statusColumn,
      schedulingFlowsEnabled ? scheduleColumn : null,
      errorColumn
    ]);
  }

}
